// packages/core/src/process-walker-types.ts
//
// Types for the process-walker engine. Pure; no runtime dependencies.

import type { QualifiedName } from "./types.js";

/**
 * Stable, path-based identifier for any node visited by the walker.
 * Examples:
 *   "p:#proc01"                                — process root
 *   "p:#proc01/each:0"                         — first each block
 *   "p:#proc01/each:0/step:0"                  — first call inside that each
 *   "p:#proc01/try:1/catch:InsufficientFunds"  — a catch branch
 *   "p:#proc01/switch:2/case:Stripe/step:0"
 *   "p:#proc01/do:4"                           — do invocation
 *   "p:#proc01/do:4/step:0"                    — step inside expanded do body
 */
export type NodeId = string;

/** Whether a walker nodeId is an `await` wait step — its leaf segment is
 *  `await:<n>` (the emitter stamps the IR kind, so a NOTE whose text merely
 *  starts "await" is not matched). Accepts `undefined` for hand-built walks
 *  whose steps omit a nodeId. The one place this IR-format check lives. */
export function isAwaitNodeId(nodeId: string | undefined): boolean {
  return nodeId?.split("/").pop()?.startsWith("await:") === true;
}

/**
 * User-controlled selections fed back into the walker. Default behavior
 * (when an entry is missing): first declared branch is selected, all
 * subprocess invocations are collapsed.
 */
export interface WalkerState {
  /** Per-branching-node ID → chosen branch label. */
  readonly branchSelection: ReadonlyMap<NodeId, string>;
  /** `do` invocation nodeIds the user has expanded. */
  readonly expandedSubprocesses: ReadonlySet<NodeId>;
}

/**
 * Lookup context the walker needs that isn't on the process `ProcessModel`
 * directly — the subprocess IR bodies callable from it, keyed by name (the
 * caller resolves scope, e.g. via `subprocessScopeFor`, and pairs to IR).
 */
export interface WalkerContext {
  /** Subprocess IR models keyed by name. Both top-level and nested. */
  readonly subprocessByName: ReadonlyMap<string, import("./process-model.js").SubprocessModel>;
  /** v0.9.0 §2.5: `do <Process>` fallback targets — consulted only when the
   *  name misses `subprocessByName` (a same-name subprocess always wins). */
  readonly processByName?: ReadonlyMap<string, import("./process-model.js").ProcessModel>;
}

/**
 * Linear step emitted into `WalkResult.steps`. Step indices are dense
 * and stable for a given (process, state) pair. Only steps from
 * selected branches are emitted.
 */
export interface WalkStep {
  readonly kind: "step";
  readonly nodeId: NodeId;
  readonly stepIndex: number;
  readonly from: QualifiedName;
  /** Callee for simple steps; for note steps `to` repeats `from` so
   *  every step has a lane anchor. */
  readonly to: QualifiedName;
  /** v0.9.0 §1.5: when the caller/callee was written as a membership INSTANCE
   *  name (`active`), `from`/`to` hold the resolved member module while this
   *  holds the alias — views label the lane/edge with the alias when present. */
  readonly fromInstance?: string;
  readonly toInstance?: string;
  /** True when the callee's interface is UNSPECIFIED (a synthesized anonymous
   *  TODO leaf) — `to` then holds the bare member/module path with no trailing
   *  interface segment, so lane resolution must not strip its last segment. */
  readonly toAnonymous?: boolean;
  /** Call direction relative to the process's owner (PROCESS.md §1): an
   *  inbound step (`Them > Us.x`) is a wait for an incoming call. Set on call
   *  steps only (notes/awaits/gos have no hop). */
  readonly direction?: import("./process-model.js").CallDirection;
  readonly bind?: string;
  readonly args?: readonly string[];
  /** `as <name>` step alias — the re-entry target a `go <name>` resumes at
   *  (PROCESS.md §7). Surfaced so the views can link `go` ↔ its `as` step. */
  readonly asName?: string;
  /** When set, this step is a `go <target>` re-entry marker — the label of the
   *  `as` step it resumes at. The views render it as a back-reference, not a
   *  plain comment. */
  readonly reentryTarget?: string;
  /** When set, this step is a free-form note action — the text holds
   *  the human-readable description; `from` is the actor lane. */
  readonly noteText?: string;
  /** v0.9.0 §2.6: this row is ownerless flow PROSE (noteText holds it) —
   *  positional annotation, empty lanes, no edge. */
  readonly prose?: boolean;
  /** Trailing freehand comment authored on the call / `go` step (`> Callee
   *  "note"`, PROCESS.md §1). The views render it as the edge's midpoint label
   *  (ellipsed) and in the step's sidebar row. On a chain it is set on the last
   *  hop's step only. Distinct from {@link noteText} (a note is a whole action;
   *  a comment annotates an edge). */
  readonly comment?: string;
  /** Raw `latency` field value from an annotated note (`Ops "review" { latency: 2h }`).
   *  Set only on note steps with a `latency` field; absent on all other steps. */
  readonly noteLatency?: string;
  readonly modifiers: StepModifiers;
}

/** A `go <target>` re-entry (PROCESS.md §7) — the only back-edge in the model.
 *  Resolved within the process: `toNodeId` is the `as`-named step it resumes at
 *  when found among the already-walked steps. */
export interface Reentry {
  readonly fromNodeId: NodeId;
  readonly target: string;
  readonly toNodeId?: NodeId;
}

/**
 * The compensation flow of one `reversible` saga (PROCESS.md §8), surfaced so the
 * flow view can draw the backward run the forward emission no longer interleaves.
 * The forward steps render on the happy path; these describe the FAILURE-only
 * structure layered over them:
 *
 *   - `chain` — the compensation entries in fire order (item N's unwind … item 1's
 *     unwind), terminated by `failNodeId` (the default saga outcome). Consecutive
 *     entries form the reverse chain `u_last → … → u1 → fail`.
 *   - `drops` — per-forward-step failure entry: forward item `i` fails → drop into
 *     the unwind of the greatest `j < i` that has one (`fromNodeId → toNodeId`).
 *
 * All ids are walker nodeIds; the renderer resolves each to a flow-graph node key
 * (a step `s<idx>` or a terminator `r<idx>` — an `unwind fail` / the synth fail).
 */
export interface CompensationFrame {
  readonly reversibleId: NodeId;
  readonly failNodeId: NodeId;
  readonly chain: readonly NodeId[];
  readonly drops: readonly { readonly fromNodeId: NodeId; readonly toNodeId: NodeId }[];
}

export interface StepModifiers {
  readonly inEach: boolean;
  readonly inParallel: boolean;
  readonly parallelGroup?: NodeId;
  readonly inCatch: boolean;
  /** True for a saga **compensation** step — inside a `reversible` item's
   *  `unwind` reverse action (the failure-path undo). Lets the board style
   *  compensation distinctly from a forward step or a plain `catch`. */
  readonly compensation?: boolean;
  /** The enclosing `reversible`'s nodeId, set on every compensation step AND on
   *  each forward saga step (see `reversibleForward`) — and on comp terminator
   *  rows via the WalkNode. Scopes the comp run per saga so two reversibles in one
   *  process don't merge, and lets the flow view lift the whole backward run off
   *  the forward spine. */
  readonly reversibleId?: NodeId;
  /** True for a FORWARD step of a `reversible` item (the happy-path action a
   *  compensation undoes) — distinct from `compensation` (the reverse action).
   *  Set only on the item's forward leaf(s); lets the editor offer an `unwind`
   *  editor exactly on a reversible forward. */
  readonly reversibleForward?: boolean;
  /** For a `reversibleForward` step: the canonical text of its paired `unwind`
   *  action (`> Inventory.release`), or undefined when the item carries none.
   *  Prefills / labels the inline unwind editor without re-reading the IR. */
  readonly unwindText?: string;
  /** True for a step inside a **retry** loop's attempt (`each <n> try { … }`),
   *  so it can render distinctly from a plain `each` body. */
  readonly retry?: boolean;
  /** True for a step inside a `dist` (distributed/choreographed) region —
   *  no coordinator (PROCESS.md §2). Lets the views mark the choreography. */
  readonly dist?: boolean;
  /** Branch label of the nearest enclosing if/switch/try case. */
  readonly branchLabel?: string;
  /**
   * True when this step lies inside the currently-selected branch
   * (and all ancestor branches were also selected). False when it's
   * inside an unselected alternative emitted via `showAllBranches`.
   * Default true — top-level steps always count as selected.
   */
  readonly branchSelected?: boolean;
  /** Subprocess invocation chain, outermost first. Empty if not inside a `do`. */
  readonly subprocessChain: readonly string[];
  /** Set when this step is inside a collapsed `do`. */
  readonly collapsedSubprocessGroup?: NodeId;
}

export interface StepRange {
  readonly startIndex: number;
  readonly endIndex: number;
}

/** One branch of an `if`, `switch`, `try`, or `parallel` container. */
export interface WalkBranch {
  readonly label: string;
  /** True for the chosen branch (or every branch of a parallel). */
  readonly selected: boolean;
  readonly children: readonly WalkNode[];
}

/** Tree node — either a leaf step or a structural container. */
export type WalkNode =
  | WalkStep
  | {
      readonly kind: "each";
      readonly nodeId: NodeId;
      readonly label?: string;
      readonly source?: string;
      /** Resolved performer of the construct's owner prefix (`A each …`) —
       *  undefined for `dist`/ownerless. Views colour controls by it. */
      readonly owner?: QualifiedName;
      /** True when this loop is a RETRY (`each <bound> try … catch … else …`,
       *  PROCESS.md §4) rather than a plain `each`: on failure the catch runs and
       *  the loop RE-ATTEMPTS; on success it exits; on bound-exhaustion the
       *  `else` runs. Lets views render it distinctly (a retry badge + the else
       *  as the exhausted-exit) from a plain iteration. */
      readonly retry?: boolean;
      readonly children: readonly WalkNode[];
    }
  | {
      readonly kind: "if";
      readonly nodeId: NodeId;
      readonly owner?: QualifiedName;
      readonly branches: readonly WalkBranch[];
      readonly elseBranch?: WalkBranch;
    }
  | {
      readonly kind: "switch";
      readonly nodeId: NodeId;
      readonly owner?: QualifiedName;
      readonly head?: string;
      readonly cases: readonly WalkBranch[];
      /** `select` is inclusive (PROCESS.md §3): every matching case runs. Always
       *  true for a switch (it only comes from `select`); kept explicit so the
       *  views render all cases as live possibilities, never an exclusive pick. */
      readonly inclusive: true;
      /** How many cases run at runtime: undefined = all (bare `select`), else the
       *  `select N` / `select one` (=1) bound. The views annotate "N of M run";
       *  WHICH N is a runtime decision, so all cases stay live. */
      readonly runCount?: number;
    }
  | {
      readonly kind: "try";
      readonly nodeId: NodeId;
      readonly owner?: QualifiedName;
      readonly tryBranch: WalkBranch;
      readonly catches: readonly WalkBranch[];
    }
  | {
      readonly kind: "parallel";
      readonly nodeId: NodeId;
      readonly owner?: QualifiedName;
      readonly branches: readonly WalkBranch[];
      /** Short rendered merge threshold for the merge gateway — `join 2`,
       *  `race`, `out race`, `select 2`, `(fraud and credit) or override`.
       *  Undefined for a bare `parallel { … }` (plain AND-join, no annotation). */
      readonly mergeAspect?: string;
    }
  | {
      readonly kind: "do";
      readonly nodeId: NodeId;
      readonly target: string;
      readonly args?: readonly string[];
      /** Trailing freehand comment authored on the `do` (`do X "note"`). */
      readonly comment?: string;
      readonly expanded: boolean;
      readonly body?: readonly WalkNode[];
      readonly recursive?: boolean;
    }
  | {
      readonly kind: "terminator";
      readonly nodeId: NodeId;
      readonly type: "fail" | "finish";
      readonly reason?: string;
      /** v0.9.0 §2.4: `fail all` / `finish all` — terminates every live branch
       *  of the enclosing process, not just the current branch. */
      readonly all?: boolean;
      /** Set when this terminator is part of a saga's compensation run — an
       *  `unwind fail`, or the synthesized default `fail` that ends the backward
       *  chain. Carries the owning `reversible`'s id so the flow view lifts it off
       *  the forward spine and styles it as compensation. */
      readonly comp?: { readonly reversibleId: NodeId };
      /** True only for the synthesized default failure outcome appended to a
       *  reversible's compensation chain (not present in source IR). */
      readonly synthesized?: boolean;
    };

export interface WalkerDiagnostic {
  readonly kind: "recursion" | "unknown-subprocess";
  readonly nodeId: NodeId;
  /** Subprocess name that triggered the diagnostic. */
  readonly target: string;
  /** Expansion chain leading to the problem (outermost first). */
  readonly chain: readonly string[];
}

export interface WalkResult {
  readonly steps: readonly WalkStep[];
  readonly tree: readonly WalkNode[];
  readonly byIndex: ReadonlyMap<number, WalkStep>;
  /**
   * Owner-reassign resolver (the BPMN step→lane drag path): `stepIndex` → the
   * HOP stepId key that `mutateProcessStep({ op: "reassignStepOwner" })` targets.
   * Present ONLY for a call step whose caller carries an authored source span —
   * a single/head hop (`A > …`), never a later chain hop (implicit caller). A
   * missing entry means the step's owner is not source-rewritable (the view
   * shows a disabled drop rather than silently no-op'ing).
   */
  readonly reassignTargets: ReadonlyMap<number, string>;
  /**
   * Walk `NodeId` → IR node stepId key (the `mutateProcessStep` address). The
   * walk NodeId scheme (`call:`/`branch:`) diverges from the IR stepId scheme
   * (`simple:`/`if:`); this map bridges the two for the WYSIWYG step-edit
   * surfaces (delete/wrap/insert a clicked node). Filled for every step and
   * container node; a missing entry means the node is not directly addressable
   * (e.g. a synthetic retry sub-node).
   */
  readonly stepKeyByNodeId: ReadonlyMap<NodeId, string>;
  readonly collapsedGroups: ReadonlyMap<NodeId, StepRange>;
  readonly parallelGroups: ReadonlyMap<NodeId, StepRange>;
  readonly diagnostics: readonly WalkerDiagnostic[];
  /** `go` → `as` re-entry edges (PROCESS.md §7) for the board to draw back-arcs. */
  readonly reentries: readonly Reentry[];
  /** One entry per `reversible` saga — the backward compensation structure the
   *  flow view draws over the forward run (PROCESS.md §8). */
  readonly compensation: readonly CompensationFrame[];
  /**
   * v0.9.0 §2.1: the process's start triggers — the nodeIds of the LEADING
   * `await` steps of the root flow (the first non-await action ends the run).
   * Multiple = any-of (BPMN multiple start). Positional by construction, so a
   * process spliced via `do` contributes none (its awaits sit mid-flow in the
   * host). Empty when the process has no wait-to-start.
   */
  readonly triggers: readonly NodeId[];
}
